import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: customer pages use generated API outside src/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, FileText, Check, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import type { TPreAuthStatus } from '@/@types/claims';
import type {
  IFormField,
  IFormStep,
  IAddressValue,
  IConditionalDetailValue,
} from '@/@types/policyFormTemplates';

function formatFieldValue(field: IFormField, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';

  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'number':
    case 'textarea':
      return String(value);
    case 'date':
      return new Date(String(value)).toLocaleDateString();
    case 'select': {
      const opt = field.options?.find(o => o.value === value);
      return opt ? opt.label : String(value);
    }
    case 'checkbox':
      return value === true ? 'Yes' : 'No';
    case 'address': {
      const addr = value as IAddressValue; // AS_JUSTIFIED: preAuthApplicationData is untyped Convex any — runtime shape confirmed IAddressValue for address fields
      return [addr.street, addr.city, addr.postalCode, addr.country].filter(Boolean).join(', ');
    }
    case 'conditional_detail': {
      const cd = value as IConditionalDetailValue; // AS_JUSTIFIED: preAuthApplicationData is untyped Convex any — runtime shape confirmed IConditionalDetailValue for conditional_detail fields
      if (cd.answer === true) {
        return cd.details ? `Yes: ${cd.details}` : 'Yes';
      }
      return 'No';
    }
    default:
      return String(value);
  }
}

function isFullWidth(field: IFormField): boolean {
  return field.fieldType === 'textarea' || field.fieldType === 'address';
}

function WizardDataCards({
  steps,
  applicationValues,
}: {
  steps: IFormStep[];
  applicationValues: Record<string, unknown>;
}) {
  const populatedSteps = steps.filter(s => {
    if (s.fields.length === 0) return false;
    return s.fields.some(f => {
      const val = applicationValues[f.key];
      return val !== undefined && val !== null && val !== '';
    });
  });

  if (populatedSteps.length === 0) return null;

  return (
    <>
      {populatedSteps.map((step, i) => (
        <Card key={i} className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">{step.title}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm">
            {step.fields.map(field => {
              const val = applicationValues[field.key];
              if (val === undefined || val === null || val === '') return null;
              const formatted = formatFieldValue(field, val);
              if (!formatted) return null;
              const wide = isFullWidth(field);
              return (
                <div key={field.key} className={wide ? 'col-span-2' : ''}>
                  <span className="text-muted-foreground">{field.label}</span>
                  <p
                    className={`font-medium mt-0.5${field.fieldType === 'textarea' ? ' whitespace-pre-wrap' : ''}`}
                  >
                    {formatted}
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </>
  );
}

function preAuthStatusVariant(
  status: TPreAuthStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'approved':
      return 'default';
    case 'under_review':
      return 'secondary';
    case 'denied':
      return 'destructive';
    default:
      return 'outline';
  }
}

function StatusContext({
  status,
  approvedAmount,
  currency,
  validUntil,
  denialReason,
}: {
  status: TPreAuthStatus;
  approvedAmount?: number;
  currency: string;
  validUntil?: number;
  denialReason?: string;
}) {
  if (status === 'requested') {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
        <Clock className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          Your request is being processed. We will notify you once reviewed.
        </p>
      </div>
    );
  }
  if (status === 'under_review') {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950">
        <Clock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Your request is currently under review by the insurance team.
        </p>
      </div>
    );
  }
  if (status === 'approved') {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950">
        <div className="flex items-start gap-3 mb-3">
          <Check className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            Your pre-authorization has been approved. You can now submit a claim.
          </p>
        </div>
        {approvedAmount != null && (
          <p className="text-sm text-green-700 dark:text-green-300 ml-8">
            Approved amount:{' '}
            <span className="font-medium">
              {currency} {approvedAmount.toLocaleString()}
            </span>
          </p>
        )}
        {validUntil && (
          <p className="text-sm text-green-700 dark:text-green-300 ml-8 mt-1">
            Valid until:{' '}
            <span className="font-medium">{new Date(validUntil).toLocaleDateString()}</span>
          </p>
        )}
      </div>
    );
  }
  if (status === 'denied') {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-destructive">Your request was not approved.</p>
            {denialReason && <p className="text-sm text-muted-foreground mt-1">{denialReason}</p>}
          </div>
        </div>
      </div>
    );
  }
  if (status === 'expired') {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-900 dark:bg-orange-950">
        <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
        <p className="text-sm text-orange-700 dark:text-orange-300">
          This pre-authorization has expired. Please submit a new request if treatment is still
          needed.
        </p>
      </div>
    );
  }
  return null;
}

export function MyPreAuthDetailPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';

  const preAuthId = id as Id<'preAuthorizations'>; // AS_JUSTIFIED: id from route params, narrow to typed Id
  const preAuth = useQuery(api.insurance.preAuthorizations.get, { preAuthId });

  const template = useQuery(
    api['insurance/claimFormTemplates'].get,
    preAuth?.formTemplateId ? { templateId: preAuth.formTemplateId } : 'skip'
  );

  if (preAuth === undefined) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (preAuth === null) {
    return (
      <div className="flex-1 p-8">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="mt-8 text-center text-muted-foreground">Pre-authorization not found.</div>
      </div>
    );
  }

  const status = preAuth.status as TPreAuthStatus; // AS_JUSTIFIED: Convex return status string narrowed to TPreAuthStatus for StatusContext component
  const templateSteps = (template?.steps ?? []) as IFormStep[]; // AS_JUSTIFIED: claimFormTemplates.steps is v.optional — runtime shape is IFormStep[]
  const rawAppData = preAuth.preAuthApplicationData as Record<string, unknown> | null | undefined; // AS_JUSTIFIED: preAuthApplicationData is untyped Convex field — confirmed IPreAuthApplicationData shape at runtime
  const applicationValues = (rawAppData?.['values'] as Record<string, unknown> | undefined) ?? {}; // AS_JUSTIFIED: values key of IPreAuthApplicationData — untyped Convex storage
  const hasWizardData =
    preAuth.preAuthApplicationData != null &&
    templateSteps.length > 0 &&
    Object.keys(applicationValues).length > 0;

  return (
    <div className="flex-1 p-8 max-w-3xl">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-semibold font-mono">{preAuth.preAuthNumber}</h1>
        <Badge variant={preAuthStatusVariant(status)}>
          {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </Badge>
      </div>

      <div className="mb-6">
        <StatusContext
          status={status}
          approvedAmount={preAuth.approvedAmount}
          currency={preAuth.currency}
          validUntil={preAuth.validUntil}
          denialReason={preAuth.denialReason}
        />
      </div>

      {status === 'approved' && (
        <div className="mb-6">
          <Button onClick={() => navigate(`${basePath}/claims/new?preAuthId=${preAuth._id}`)}>
            <ExternalLink className="h-4 w-4 mr-2" />
            File a Claim
          </Button>
        </div>
      )}

      {hasWizardData ? (
        <div className="mb-6">
          <WizardDataCards steps={templateSteps} applicationValues={applicationValues} />
        </div>
      ) : (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Request Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Treatment Type</span>
              <p className="font-medium capitalize">{preAuth.treatmentType ?? '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Estimated Cost</span>
              <p className="font-medium">
                {preAuth.currency} {preAuth.estimatedCost.toLocaleString()}
              </p>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Proposed Treatment</span>
              <p className="font-medium mt-0.5">{preAuth.proposedTreatment}</p>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Proposed Provider</span>
              <p className="font-medium mt-0.5">{preAuth.proposedProvider}</p>
            </div>
            {preAuth.medicalJustification && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Medical Justification</span>
                <p className="font-medium mt-0.5 whitespace-pre-wrap">
                  {preAuth.medicalJustification}
                </p>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Submitted</span>
              <p className="font-medium">{new Date(preAuth.createdAt).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {(preAuth.supportingDocIds?.length ?? 0) > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Supporting Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(preAuth.supportingDocIds ?? []).map((docId: string) => (
                <li key={docId} className="flex items-center gap-3 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground font-mono text-xs">{docId}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {preAuth.linkedClaimId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Linked Claim</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => navigate(`${basePath}/claims/${preAuth.linkedClaimId}`)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View linked claim
            </Button>
          </CardContent>
        </Card>
      )}

      <Separator className="my-6" />
    </div>
  );
}
